import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import PropertyCard from "../../../components/PropertyCard/PropertyCard";
import Pagination from "../../../components/Pagination/Pagination";
import Loader from "../../../components/Loader/Loader";
import residentialImages from "../../../assets/images/real-estate.jpg";
import commercialImages from "../../../assets/images/real-estate2.jpg";
import { getProperties, getCities } from "../../../services/api";
import "./Projects.css";

const PAGE_SIZE = 9;

const CATEGORIES = [
  {
    id: "residential",
    slug: "residential",
    name: "Residential",
    description:
      "Skyline apartments, villas and gated communities from trusted developers.",
  },
  {
    id: "commercial",
    slug: "commercial",
    name: "Commercial",
    description: "Grade-A offices, retail plazas and investment-grade workspaces.",
  },
  {
    id: "plots",
    slug: "plots",
    name: "Plots",
    description: "Gated plots with clear titles and approved layouts.",
  },
];

const POSSESSION_OPTIONS = [
  "Ready to move",
  "Ready plots",
  "Under Construction",
  "Dec 2026",
  "Mar 2027",
  "Jun 2027",
  "Q4 2026",
];

const HERO_IMAGE = {
  residential: residentialImages,
  commercial: commercialImages,
  plots:
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80",
  default:
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80",
};

function isPublicListing(p) {
  const status = String(p.status || "").trim().toLowerCase();
  const published = !status || status === "published";
  const active = p.isActive !== false && p.isActive !== "false";
  return published && active;
}

function matchesCategory(p, categorySlug) {
  if (!categorySlug) return true;
  const cat = String(p.category || "").toLowerCase();
  const slug = categorySlug.toLowerCase();
  if (slug === "plots") return cat === "plots" || cat === "plot";
  return cat === slug;
}

export default function Projects() {
  const { category: categorySlug } = useParams();
  const [allItems, setAllItems] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    city: "",
    possession: "",
    q: "",
  });

  const category =
    CATEGORIES.find((c) => c.slug === categorySlug) || null;

  // Load properties + cities once
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [list, cities] = await Promise.all([
          getProperties(),
          getCities().catch(() => []),
        ]);
        if (cancelled) return;

        setAllItems(Array.isArray(list) ? list : []);
        setCityOptions(Array.isArray(cities) ? cities : []);
      } catch (err) {
        console.error("Projects load failed:", err);
        if (!cancelled) {
          setAllItems([]);
          setCityOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Reset page when category / filters change
  useEffect(() => {
    setPage(1);
  }, [categorySlug, filters]);

  const items = useMemo(() => {
    let list = allItems.filter(isPublicListing);

    list = list.filter((p) => matchesCategory(p, categorySlug));

    if (filters.q.trim()) {
      const q = filters.q.toLowerCase();
      list = list.filter((p) => {
        const hay = `${p.title || p.name || ""} ${
          p.developer || p.builder || ""
        } ${p.locality || p.location || ""} ${p.city || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (filters.city) {
      const cityFilter = filters.city.toLowerCase();
      list = list.filter((p) => {
        const city = String(p.city || "").toLowerCase();
        const slug = String(
          cityOptions.find(
            (c) =>
              String(c.name || "").toLowerCase() === city ||
              String(c.slug || "").toLowerCase() === cityFilter
          )?.slug || ""
        ).toLowerCase();
        return (
          city === cityFilter ||
          slug === cityFilter ||
          city.includes(cityFilter)
        );
      });
    }

    if (filters.possession) {
      const pos = filters.possession.toLowerCase();
      list = list.filter((p) =>
        String(p.possessionStatus || p.possession || "")
          .toLowerCase()
          .includes(pos)
      );
    }

    return list;
  }, [allItems, categorySlug, filters, cityOptions]);

  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const paged = useMemo(
    () => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [items, page]
  );

  const cityCount = useMemo(
    () => new Set(items.map((p) => p.city).filter(Boolean)).size,
    [items]
  );

  const heroSrc = HERO_IMAGE[categorySlug] || HERO_IMAGE.default;

  return (
    <div className="projects-page">
      <Navbar />

      <section className="pj-hero">
        <div className="pj-hero-bg">
          <img
            key={categorySlug || "all"}
            src={heroSrc}
            alt=""
            width={1920}
            height={1080}
            loading="eager"
          />
        </div>
        <div className="pj-hero-grain" />

        <div className="pj-hero-inner">
          <span className="pj-eyebrow">
            <span className="dot" /> Curated Inventory
          </span>

          <h1 className="pj-title">
            {category?.name ?? "Projects"} <em>worth owning.</em>
          </h1>

          <p className="pj-sub">
            {category?.description ??
              "Curated projects from India's most trusted developers — verified, negotiated and advisory-backed."}
          </p>

          <div className="pj-stats">
            <div className="pj-stat">
              <div className="pj-stat-num">
                {loading ? "—" : items.length}
              </div>
              <div className="pj-stat-label">Live projects</div>
            </div>
            <div className="pj-stat">
              <div className="pj-stat-num">
                {loading ? "—" : cityCount || "—"}
              </div>
              <div className="pj-stat-label">Cities covered</div>
            </div>
            <div className="pj-stat">
              <div className="pj-stat-num">100%</div>
              <div className="pj-stat-label">RERA verified</div>
            </div>
            <div className="pj-stat">
              <div className="pj-stat-num">₹0</div>
              <div className="pj-stat-label">Brokerage</div>
            </div>
          </div>

          <div className="pj-tabs">
            <Link
              to="/projects"
              className={`pj-tab ${!categorySlug ? "is-active" : ""}`}
            >
              All
            </Link>
            {CATEGORIES.map((c) => (
              <Link
                key={c.id}
                to={`/projects/${c.slug}`}
                className={`pj-tab ${
                  c.slug === categorySlug ? "is-active" : ""
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="pj-hero-fade" />
      </section>

      <section className="pj-body">
        <div className="pj-container">
          <div className="pj-filters">
            <input
              type="text"
              placeholder="Search by name, builder, area…"
              value={filters.q}
              onChange={(e) =>
                setFilters({ ...filters, q: e.target.value })
              }
              className="filter-input filter-search"
            />

            <select
              value={filters.city}
              onChange={(e) =>
                setFilters({ ...filters, city: e.target.value })
              }
              className="filter-input"
            >
              <option value="">All cities</option>
              {cityOptions.map((c) => (
                <option
                  key={c.id || c.name}
                  value={c.slug || c.name}
                >
                  {c.name}
                </option>
              ))}
            </select>

            <select
              value={filters.possession}
              onChange={(e) =>
                setFilters({ ...filters, possession: e.target.value })
              }
              className="filter-input"
            >
              <option value="">Any possession</option>
              {POSSESSION_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>

          {loading && <Loader label="Loading projects…" />}

          {!loading && items.length === 0 && (
            <div className="pj-empty">No projects match these filters.</div>
          )}

          {!loading && items.length > 0 && (
            <>
              <div className="pj-grid">
                {paged.map((p) => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>

              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={setPage}
              />
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}